<template>
  <button
    :class="[
      'btn',
      `btn--${variant}`,
      block && `btn--block${typeof block === 'string' ? `-${block}` : ''}`,
      label && 'btn--label',
      corner && 'btn--corner'
    ]"
    :disabled="disabled"
    :type="submit ? 'submit' : 'button'"
  >
    <slot />
    <Transition name="zoom">
      <span v-if="badge" class="btn__badge"></span>
    </Transition>
  </button>
</template>

<script setup>
const props = defineProps({
  variant: {
    type: String,
    default: 'primary',
    validator: (value) => [
      'primary',
      'success',
      'danger',
      'warning',
      'info',
      'default',
      'outline-primary',
      'outline-success',
      'outline-danger',
      'outline-warning',
      'outline-info',
      'outline-default',
      'link-primary',
      'link-success',
      'link-danger',
      'link-warning',
      'link-info',
      'link-default',
      'reset',
      'center'
    ].includes(value)
  },
  block: {
    type: [Boolean, String],
    default: false,
    validator: (value) => [
      true,
      false,
      'sm',
      'md',
      'lg',
      'xl'
    ].includes(value)
  },
  label: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  },
  submit: {
    type: Boolean,
    default: false
  },
  badge: {
    type: Boolean,
    default: false
  },
  corner: {
    type: Boolean,
    default: false
  }
})
</script>


<style lang="scss">
$btn-colors: (
  "primary": ($purple-400, $purple-500),
  "success": ($green-400,  $green-500),
  "danger": ($red-400, $red-500),
  "warning": ($orange-400, $orange-500),
  "info": ($blue-400, $blue-500),
  "default": (inherit, inherit)
);

$btn-height: $component-height;
$btn-padding-x: map-get($spacers, 2);
$btn-padding-y: map-get($spacers, 2);
$btn-border-width: $border-width;
$btn-disabled-bg: $gray-100;
$btn-disabled-color: $gray-300;
$btn-badge-size: 0.375rem;
$btn-badge-offset: map-get($spacers, 2);
$btn-badge-color: $danger;
$btn-badge-border: 2px;
$btn-corner-size: 0.5rem;
$btn-corner-color: $gray-200;

@mixin button-variant($color, $hover-color) {
  color: $white;
  background-color: $color;
  border-color: $color;
  @include hover-active-focus() {
    background-color: $hover-color;
    border-color: $hover-color;
  }
  &:disabled {
    color: $btn-disabled-color;
    background-color: $btn-disabled-bg;
    border-color: $btn-disabled-bg;
  }
}

@mixin button-variant-outline($color, $hover-color) {
  color: $color;
  background-color: $white;
  @include hover-active-focus() {
    color: $hover-color;
  }
  &:disabled {
    color: $btn-disabled-color;
    border-color: $btn-disabled-bg;
  }
}

@mixin button-variant-link($color, $hover-color) {
  color: $color;
  border-color: transparent;
  @include hover-active-focus() {
    color: $hover-color;
  }
  &:disabled {
    color: $btn-disabled-color;
  }
}

.btn {
  transition-property: background-color, color, border-color;
  transition-duration: $duration-fast;
  position: relative;
  display: inline-block;
  color: $body-color;
  background-color: transparent;
  text-align: center;
  font-weight: $font-weight-bold;
  white-space: nowrap;
  height: $btn-height;
  line-height: 1;
  vertical-align: middle;
  touch-action: manipulation;
  border: $btn-border-width solid;
  border-radius: $border-radius;
  padding: $btn-padding-y $btn-padding-x;
  cursor: pointer;

  @include hover-active-focus {
    text-decoration: none;
  }

  @include user-select;

  &:focus {
    outline: 0;
  }

  &:disabled {
    pointer-events: none;
    cursor: not-allowed;
  }

  @each $color, $list in $btn-colors {
    &--#{$color} {
      @include button-variant(nth($list, 1), nth($list, 2));
    }
    &--outline-#{$color} {
      @include button-variant-outline(nth($list, 1), nth($list, 2));
    }
    &--link-#{$color} {
      @include button-variant-link(nth($list, 1), nth($list, 2));
    }
  }

  &--block {
    display: block;
    width: 100%;
    @each $bp, $value in $breakpoints {
      @media (max-width: $value) {
        &-#{$bp} {
          display: block;
          width: 100%;
        }
      }
    }
  }

  &--reset {
    color: inherit;
    height: auto;
    padding: 0;
    margin: 0;
    border: 0;
  }

  &--label {
    span {
      display: none;
    }
    @media (min-width: $breakpoint-md) {
      display: inline-flex;
      justify-content: center;
      align-items: center;
      span {
        display: inline;
      }
    }
  }

  &--center {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  @at-root span#{&} {
    font-weight: 400;
    cursor: default;
    pointer-events: none;
  }

  .icon,
  span,
  div {
    pointer-events: none;
  }

  &--danger {
    &:disabled {
      background: $red-200;
      color: $danger;
    }
  }

  &--link-danger {
    &:disabled {
      color: $danger;
    }
  }

  &__badge {
    position: absolute;
    top: $btn-badge-offset;
    right: $btn-badge-offset;
    width: $btn-badge-size;
    height: $btn-badge-size;
    border-radius: 50%;
    background: $btn-badge-color;
    box-shadow: 0 0 0 $btn-badge-border $white;
    pointer-events: none;
    animation-name: bounce;
    animation-duration: $duration-slow;
  }

  &--corner {
    position: relative;
    &:after {
      content: '';
      position: absolute;
      top: ($btn-border-width * -1);
      right: ($btn-border-width * -1);
      width: 0;
      height: 0;
      border-style: solid;
      border-width: $btn-corner-size 0 0 $btn-corner-size;
      border-color: $btn-corner-color transparent; 
    }
  }
}
</style>
